import { Form } from "antd";
import { useState, useEffect } from "react";
import {
  deleteCategories,
  updateCategories,
  addCategory,
} from "../repository/data.repository";
import { Category } from "../repository/data.default";
import DynamicForm from "../controls/DynamicForm";
import { CategoriesForm } from "./CategoriesFormDialog.default";
import InModal from "../controls/InModal";
import { modalTitle } from "../controls/InModal.default";
import ModalFooter from "../controls/ModalFooter";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import { ContentType } from "../repository/data.default";
import { getProblemMessage } from "../utils/http";
import { enumRequest } from "../utils/options";

export interface CategoriesFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: Category;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function CategoriesFormDialog(props: CategoriesFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<CategoriesForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (props.open) {
      form.setFieldsValue({
        ...props.initialValues,
        // ContentType is a combobox — feed it as an {value, display} option so the current
        // value is pre-selected (and editable; it was previously not editable at all).
        contentType: props.initialValues?.contentType
          ? {
              value: props.initialValues.contentType,
              display: props.initialValues.contentType,
            }
          : undefined,
      });
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  function onError(error: unknown): void {
    setLoading(false);
    setErrorMessage(getProblemMessage(error, "Something went wrong, try again"));
  }

  function onSubmit(): void {
    const { name, description, code, contentType } = values;
    setLoading(true);
    setErrorMessage(null);
    if (props.mode === "add") {
      addCategory({
        name,
        description,
        code,
        contentType: contentType?.value,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else if (props.mode === "delete") {
      deleteCategories(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else if (props.mode === "update") {
      updateCategories({
        uid: props.initialValues?.uid,
        rowVersion: props.initialValues?.rowVersion,
        name,
        description,
        code,
        contentType: contentType?.value,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else {
      setLoading(false);
      props.onFinish?.();
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setValid(false);
    setErrorMessage(null);
    setLoading(false);
    form.resetFields();
  }

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Category", props.initialValues?.name)}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode === "add" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "textbox",
                    name: "name",
                    label: "Name",
                    rules: [
                      {
                        required: true,
                        message: "Name is required",
                      },
                    ],
                  },
                  {
                    type: "textbox",
                    name: "description",
                    label: "Description",
                    rules: [
                      {
                        required: true,
                        message: "Description is required",
                      },
                    ],
                  },
                  {
                    type: "textbox",
                    name: "code",
                    label: "Code",
                    rules: [
                      {
                        required: true,
                        message: "Code is required",
                      },
                    ],
                  },
                  {
                    type: "combobox",
                    name: "contentType",
                    label: "Content Type",
                    rules: [
                      {
                        required: true,
                        message: "Content Type is required",
                      },
                    ],
                    request: enumRequest(ContentType),
                  },
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "update" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "textbox",
                    name: "name",
                    label: "Name",
                    rules: [
                      {
                        required: false,
                        message: "Name is required",
                      },
                    ],
                  },
                  {
                    type: "textbox",
                    name: "description",
                    label: "Description",
                    rules: [
                      {
                        required: false,
                        message: "Description is required",
                      },
                    ],
                  },
                  {
                    type: "combobox",
                    name: "contentType",
                    label: "Content Type",
                    request: enumRequest(ContentType),
                  },
                ],
              },
            ],
          }}
        />
      )}

      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="category"
          entityName={props.initialValues?.name}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default CategoriesFormDialog;
